/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { CurrentItemVariant } from '#UNSAFE_Collection';
import type { RowStyles } from '../themes/ListRowStyles.css';
import type { ItemPadding } from '../List.types';

import { textAlignStyles, verticalAlignStyles } from '#PRIVATE_TableCommon/themes/Common.css';
import { listCellStyles, listCellStylesHC } from '../themes/ListCellStyles.css';
import { listRowStyles, listRowStylesHC } from '../themes/ListRowStyles.css';

type ListRowThemingProps = {
  isFinalRowIndex: boolean;
  hasTopGridline: boolean;
  hasBottomGridline: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isShowFocusRing: boolean;
  isSelectionEnabled: boolean;
  isSingleSelection: boolean;
  isActive: boolean;
  isHover: boolean;
  isPseudoHover: boolean;
  isHighContrast: boolean;
  variant?: CurrentItemVariant;
  padding?: ItemPadding;
};

/**
 * Helper function to generate the class array for a list row.
 */
export const getListRowClassArray = ({
  isFinalRowIndex,
  hasTopGridline,
  hasBottomGridline,
  isSelected,
  isPreviousSelected,
  isShowFocusRing,
  isSelectionEnabled,
  isSingleSelection,
  isActive,
  isHover,
  isPseudoHover,
  isHighContrast,
  variant,
  padding
}: ListRowThemingProps) => {
  const rowStyles = isHighContrast ? listRowStylesHC : listRowStyles;
  const classArray = [rowStyles.base];

  // Padding styling logic
  const { isTopPadding, isBottomPadding, isStartPadding, isEndPadding } = _getPaddingInfo(padding);
  classArray.push(isStartPadding ? rowStyles.startPadding : rowStyles.startNoPadding);
  classArray.push(isEndPadding ? rowStyles.endPadding : rowStyles.endNoPadding);

  _applyFocusStyling(classArray, rowStyles, isShowFocusRing);

  // Horizontal and vertical alignment styling logic
  classArray.push(textAlignStyles['start']);
  classArray.push(verticalAlignStyles['center']);

  // Interaction styling logic
  if (isSelectionEnabled) {
    if (isActive) {
      classArray.push(rowStyles.active);
    } else if (isHover) {
      classArray.push(rowStyles.hover);
    } else if (isPseudoHover) {
      classArray.push(rowStyles.pseudoHover);
    }
  }
  if (variant === 'highlight') {
    classArray.push(rowStyles.rowHighlight);
  }

  // Selected Rows and Horizontal Gridline styling logic
  let hasBackground = false;
  if (isSelected) {
    hasBackground = true;
    classArray.push(rowStyles.selectedBackground);
  }
  if (isSingleSelection && (isSelected || isPreviousSelected)) {
    classArray.push(rowStyles.horizontalSelectedGridTop);
    classArray.push(
      isTopPadding ? rowStyles.selectedBorderTopPadding : rowStyles.selectedBorderTopNoPadding
    );
  } else if (hasTopGridline) {
    classArray.push(rowStyles.horizontalGridTop);
    classArray.push(isTopPadding ? rowStyles.borderTopPadding : rowStyles.borderTopNoPadding);
  } else {
    classArray.push(isTopPadding ? rowStyles.topSpacerPadding : rowStyles.topSpacerNoPadding);
  }
  if (isFinalRowIndex) {
    classArray.push(rowStyles.lastRow);
    if (isSingleSelection && isSelected) {
      classArray.push(rowStyles.horizontalSelectedGridBottom);
      classArray.push(
        isBottomPadding
          ? rowStyles.selectedBorderBottomPadding
          : rowStyles.selectedBorderBottomNoPadding
      );
    } else if (hasBottomGridline) {
      classArray.push(rowStyles.horizontalGridBottom);
      classArray.push(
        isBottomPadding ? rowStyles.borderBottomPadding : rowStyles.borderBottomNoPadding
      );
    } else {
      classArray.push(
        isBottomPadding ? rowStyles.bottomSpacerPadding : rowStyles.bottomSpacerNoPadding
      );
    }
  } else {
    classArray.push(rowStyles.notLastRow);
    classArray.push(isBottomPadding ? rowStyles.bottomPadding : rowStyles.bottomNoPadding);
  }

  if (!hasBackground) {
    classArray.push(rowStyles.background);
  }
  return classArray;
};

type ListCellThemingProps = {
  isHighContrast: boolean;
};

/**
 * Helper function to generate the class array for a list cell.
 */
export const getListCellClassArray = ({ isHighContrast }: ListCellThemingProps) => {
  const cellStyles = isHighContrast ? listCellStylesHC : listCellStyles;
  const classArray = [cellStyles.base];
  return classArray;
};

const _getPaddingInfo = (padding?: ItemPadding) => {
  const isPaddingEnabled = padding === 'enabled';
  const isComplexPadding = padding instanceof Object;

  const isBottomPadding = isPaddingEnabled || (isComplexPadding && padding?.bottom === 'enabled');
  const isEndPadding = isPaddingEnabled || (isComplexPadding && padding?.end === 'enabled');
  const isStartPadding = isPaddingEnabled || (isComplexPadding && padding?.start === 'enabled');
  const isTopPadding = isPaddingEnabled || (isComplexPadding && padding?.top === 'enabled');

  return { isTopPadding, isBottomPadding, isStartPadding, isEndPadding };
};

const _applyFocusStyling = (
  classArray: string[],
  elementStyles: RowStyles,
  isShowFocusRing?: boolean
) => {
  if (isShowFocusRing) {
    classArray.push(elementStyles.focused);
  }
};
